import React from 'react';
import { Button, Card, DatePicker, Empty, Form, Icon, message, Spin, Table, Tooltip } from 'antd';
import moment from 'moment';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import axios from '../../util/Api';

const { RangePicker } = DatePicker;

class SalesReport extends React.Component {
  state = {
    loading: false,
    reportData: [],
    selectedRange: [],
  };

  handleRangeChange = (dates) => {
    this.setState({ selectedRange: dates || [] });
  };

  fetchReport = async () => {
    const { selectedRange } = this.state;

    if (!selectedRange || selectedRange.length !== 2) {
      message.error('Please select a date range.');
      return;
    }

    this.setState({ loading: true });

    try {
      const payload = {
        fromDate: moment(selectedRange[0]).startOf('day').valueOf(),
        toDate: moment(selectedRange[1]).endOf('day').valueOf(),
      };
      const response = await axios.post('/sale-order/sales-report', payload);
      this.setState({ reportData: response.data || [] });
      message.success('Sales report loaded successfully.');
    } catch (error) {
      message.error('Failed to load sales report.');
    } finally {
      this.setState({ loading: false });
    }
  };

  downloadExcel = () => {
    const { reportData } = this.state;

    if (!reportData || !reportData.length) {
      message.error('No report data available to download.');
      return;
    }

    const rows = reportData.map((item, index) => ({
      'Sl No': index + 1,
      'Asset Code': item.assetCode || '',
      'Alt Name': item.altName || '',
      'Owner Code': item.ownerCode || '',
      'Sale Date': item.saleDate ? moment(item.saleDate).format('DD-MM-YYYY') : '',
      Vendor: item.vendor || '',
      'Sale Rate': item.saleRate || 0,
      'Sale Amount': item.saleAmount || 0,
      Documents: item.documents && item.documents.length
        ? item.documents.map(doc => doc.path || doc.fileName || doc.name).filter(Boolean).join(', ')
        : '',
    }));

    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Report');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, `sales-report-${Date.now()}.xlsx`);
  };

  renderDocuments = (documents) => {
    if (!documents || !documents.length) {
      return '-';
    }

    return documents.map((document, index) => {
      if (!document.path) {
        return <span key={`${document._id || index}`}>{document.name || document.fileName || `Document ${index + 1}`}</span>;
      }

      return (
        <a
          key={`${document._id || index}`}
          href={document.path}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block' }}
        >
          View {documents.length > 1 ? index + 1 : ''}
        </a>
      );
    });
  };

  render() {
    const { loading, reportData, selectedRange } = this.state;

    const columns = [
      {
        title: 'Asset Code',
        dataIndex: 'assetCode',
        key: 'assetCode',
      },
      {
        title: 'Alt Name',
        dataIndex: 'altName',
        key: 'altName',
      },
      {
        title: 'Owner Code',
        dataIndex: 'ownerCode',
        key: 'ownerCode',
      },
      {
        title: 'Sale Date',
        dataIndex: 'saleDate',
        key: 'saleDate',
        render: value => value ? moment(value).format('DD-MM-YYYY') : '-',
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        key: 'vendor',
      },
      {
        title: 'Sale Rate',
        dataIndex: 'saleRate',
        key: 'saleRate',
        align: 'right',
      },
      {
        title: 'Sale Amount',
        dataIndex: 'saleAmount',
        key: 'saleAmount',
        align: 'right',
      },
      {
        title: 'Document',
        dataIndex: 'documents',
        key: 'documents',
        render: this.renderDocuments,
      },
    ];

    return (
      <Card className="gx-card-metrics" title="Sales Report">
        <Spin spinning={loading}>
          <Form layout="inline" style={{ marginBottom: 20 }}>
            <Form.Item label="Sales Date Range">
              <RangePicker
                value={selectedRange}
                format="DD-MM-YYYY"
                onChange={this.handleRangeChange}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" onClick={this.fetchReport} loading={loading}>
                OK
              </Button>
            </Form.Item>
            <Form.Item>
              <Tooltip title={!reportData.length ? 'Generate report before download' : 'Download Excel'}>
                <Button disabled={!reportData.length || loading} onClick={this.downloadExcel}>
                  <Icon type="download" /> Download Excel
                </Button>
              </Tooltip>
            </Form.Item>
          </Form>

          {reportData && reportData.length ? (
            <Table
              rowKey={(record, index) => `${record.saleOrderId}-${record.assetId}-${index}`}
              dataSource={reportData}
              columns={columns}
              pagination={{ pageSize: 20 }}
              bordered
              scroll={{ x: 1000 }}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sales records loaded" />
          )}
        </Spin>
      </Card>
    );
  }
}

export default SalesReport;
